import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { getActiveHoliday } from '../lib/holiday';
import { getStockMap, resolveStock, isOutOfStock } from '../lib/stock';
import { withTimeout } from '../lib/withTimeout';
import ClosedScreen from '../components/ClosedScreen';
import StoreNav from '../components/StoreNav';
import { useCart } from '../context/CartContext';

const ICE_LEVELS = ['Normal', 'Sedikit', 'Tanpa Es'];
const SUGAR_LEVELS = ['Normal', 'Sedikit', 'Tanpa Gula'];

function VariantRow({ variant, basePrice, selected, onSelect }) {
  return (
    <button onClick={onSelect} style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%',
      padding: '13px 16px', borderRadius: 12, textAlign: 'left',
      border: selected ? '2px solid var(--primary)' : '2px solid var(--line)',
      background: selected ? 'var(--primary-soft)' : 'var(--paper)',
      transition: 'border-color .15s, background .15s',
    }}>
      <div style={{ fontWeight: 600, fontSize: '.9rem', color: 'var(--ink)' }}>{variant.name}</div>
      <div style={{ fontFamily: "'Baloo 2',sans-serif", fontWeight: 700, fontSize: '1.05rem', color: 'var(--primary)' }}>
        Rp {(basePrice + (variant.extra_price || 0)).toLocaleString('id-ID')}
      </div>
    </button>
  );
}

function LevelPicker({ label, levels, value, onChange }) {
  return (
    <div style={{ marginBottom: 22 }}>
      <div style={{ fontSize: '.74rem', color: 'var(--ink-soft)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 10 }}>{label}</div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {levels.map(l => (
          <button key={l} onClick={() => onChange(l)} style={{
            padding: '8px 14px', borderRadius: 999, fontSize: '.8rem',
            border: value === l ? '2px solid var(--primary)' : '1.5px solid var(--line)',
            background: value === l ? 'var(--primary-soft)' : 'var(--paper)',
            color: value === l ? 'var(--primary)' : 'var(--ink-soft)', fontWeight: value === l ? 700 : 400,
          }}>{l}</button>
        ))}
      </div>
    </div>
  );
}

export default function MenuDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { addItem } = useCart();

  const [item, setItem] = useState(null);
  const [variants, setVariants] = useState([]);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [iceLevel, setIceLevel] = useState('Normal');
  const [sugarLevel, setSugarLevel] = useState('Normal');
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  const [stockMap, setStockMap] = useState({});
  const [holiday, setHoliday] = useState(undefined);

  useEffect(() => { load(); }, [id]);

  useEffect(() => {
    const row = resolveStock(stockMap, item?.id, variants.length > 0 ? selectedVariant?.id : null);
    if (row && qty > row.qty) setQty(Math.max(1, row.qty));
    // eslint-disable-next-line
  }, [selectedVariant, stockMap]);

  async function load() {
    const activeHoliday = await withTimeout(getActiveHoliday(), 6000, null);
    setHoliday(activeHoliday || null);
    if (activeHoliday) return;

    const { data: m } = await supabase.from('menu_items').select('*').eq('id', id).single();
    setItem(m);
    if (m) {
      const { data: vs } = await supabase.from('menu_variants').select('*').eq('menu_item_id', m.id).order('sort_order');
      setVariants(vs || []);
      if (vs && vs.length > 0) setSelectedVariant(vs[0]);
      const map = await getStockMap([m.id]);
      setStockMap(map);
    }
  }

  if (holiday === undefined) {
    return (
      <div className="store" style={{ minHeight: '100vh' }}>
        <StoreNav />
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--ink-soft)' }}>Memuat...</div>
      </div>
    );
  }

  if (holiday) {
    return <ClosedScreen holiday={holiday} />;
  }

  if (!item) {
    return (
      <div className="store" style={{ minHeight: '100vh' }}>
        <StoreNav />
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--ink-soft)' }}>Memuat...</div>
      </div>
    );
  }

  const hasVariants = variants.length > 0;
  const unitPrice = item.base_price + (hasVariants ? (selectedVariant?.extra_price || 0) : 0);
  const subtotal = unitPrice * qty;

  const stockRow = resolveStock(stockMap, item.id, hasVariants ? selectedVariant?.id : null);
  const outOfStock = isOutOfStock(stockRow);
  const stockLeft = stockRow ? Math.max(0, stockRow.qty) : null; // null = tidak dilacak (unlimited)

  const noteParts = [];
  if (item.has_ice) noteParts.push(`Es: ${iceLevel}`);
  if (item.has_sugar) noteParts.push(`Gula: ${sugarLevel}`);
  const note = noteParts.join(', ');

  function handleAdd() {
    addItem({
      kind: 'pos',
      product_id: item.id,
      product_name: item.name,
      variant_id: hasVariants ? selectedVariant.id : null,
      variant_name: hasVariants ? selectedVariant.name : '',
      note,
      price: unitPrice,
      hpp: item.hpp || 0,
      emoji: item.emoji,
      swatch: item.swatch,
    }, qty);
    setAdded(true);
    setTimeout(() => nav('/cart'), 500);
  }

  return (
    <div className="store" style={{ minHeight: '100vh', paddingBottom: 100 }}>
      <StoreNav />
      <div style={{ maxWidth: 960, margin: '0 auto', padding: '24px 20px 40px' }}>
        <button onClick={() => nav(-1)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'transparent', border: 'none', color: 'var(--ink-soft)', fontSize: '.82rem', marginBottom: 20, padding: 0, cursor: 'pointer' }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
          Kembali
        </button>

        <div className="pd-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 40 }}>
          <div style={{ width: '100%', aspectRatio: '4/4', borderRadius: 20, overflow: 'hidden', background: item.image_url ? undefined : (item.swatch || 'var(--sand)'), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '4.5rem' }}>
            {item.image_url
              ? <img src={item.image_url} alt={item.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : item.emoji}
          </div>

          <div>
            <div style={{ fontSize: '.74rem', color: 'var(--ink-soft)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 8 }}>{item.category}</div>
            <h1 style={{ fontFamily: "'Baloo 2',sans-serif", fontWeight: 700, fontSize: '2rem', color: 'var(--ink)', marginBottom: 10 }}>{item.name}</h1>

            {hasVariants ? (
              <div style={{ marginBottom: 22 }}>
                <div style={{ fontSize: '.74rem', color: 'var(--ink-soft)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 10 }}>Pilih Varian</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
                  {variants.map(v => (
                    <VariantRow key={v.id} variant={v} basePrice={item.base_price} selected={selectedVariant?.id === v.id} onSelect={() => setSelectedVariant(v)} />
                  ))}
                </div>
              </div>
            ) : (
              <div style={{ fontFamily: "'Baloo 2',sans-serif", fontWeight: 700, fontSize: '1.3rem', color: 'var(--primary)', marginBottom: 22 }}>
                Rp {unitPrice.toLocaleString('id-ID')}
              </div>
            )}

            {item.has_ice && <LevelPicker label="Level Es" levels={ICE_LEVELS} value={iceLevel} onChange={setIceLevel} />}
            {item.has_sugar && <LevelPicker label="Level Gula" levels={SUGAR_LEVELS} value={sugarLevel} onChange={setSugarLevel} />}

            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 8 }}>
              <span style={{ fontSize: '.78rem', color: 'var(--ink-soft)', fontWeight: 600 }}>Jumlah</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, border: '1.5px solid var(--line)', borderRadius: 999, padding: '4px 6px' }}>
                <button onClick={() => setQty(q => Math.max(1, q - 1))} disabled={outOfStock} style={{ width: 30, height: 30, borderRadius: '50%', background: 'var(--sand)', border: 'none', fontSize: '1rem', cursor: outOfStock ? 'default' : 'pointer' }}>−</button>
                <span style={{ minWidth: 20, textAlign: 'center', fontWeight: 600 }}>{qty}</span>
                <button onClick={() => setQty(q => stockLeft != null ? Math.min(stockLeft, q + 1) : q + 1)} disabled={outOfStock || (stockLeft != null && qty >= stockLeft)} style={{ width: 30, height: 30, borderRadius: '50%', background: 'var(--sand)', border: 'none', fontSize: '1rem', cursor: (outOfStock || (stockLeft != null && qty >= stockLeft)) ? 'default' : 'pointer' }}>+</button>
              </div>
            </div>
            {!outOfStock && stockLeft != null && stockLeft <= 5 && (
              <div style={{ fontSize: '.76rem', color: '#B26A00', marginBottom: 16 }}>
                Tersisa {stockLeft} porsi hari ini
              </div>
            )}
            {!outOfStock && (stockLeft == null || stockLeft > 5) && <div style={{ marginBottom: 16 }} />}

            <button onClick={handleAdd} disabled={added || outOfStock} style={{
              width: '100%', padding: '15px', borderRadius: 999, border: 'none',
              background: outOfStock ? '#CFC9C0' : (added ? '#2E8B57' : 'var(--primary)'), color: '#fff', fontSize: '.94rem', fontWeight: 700,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              cursor: (added || outOfStock) ? 'default' : 'pointer',
            }}>
              {outOfStock ? 'Stok Habis' : (added ? 'Ditambahkan ✓' : `Tambah ke Keranjang · Rp ${subtotal.toLocaleString('id-ID')}`)}
            </button>
          </div>
        </div>
      </div>
      <style>{`@media (max-width: 700px) { .pd-grid { grid-template-columns: 1fr !important; } }`}</style>
    </div>
  );
}
